/**
 * The request trail and the frustration signals.
 *
 * The API client records the last requests: method, path, status, and the
 * correlation identifier the server returned. A problem report carries the
 * trail, so the operator can find the log lines.
 *
 * Two signals are raised on their own, without a person's report:
 *
 * - `repeated_click`: the same control is pressed three times in two seconds,
 *   unless it carries `aria-expanded` or `data-repeat-expected`, which mark a
 *   control that is meant to be pressed again.
 * - `repeated_failure`: the same write fails three times in a row.
 *
 * Nothing in the trail holds a request body, a token, or a search term.
 */

export interface TrailEntry {
  readonly at: string;
  readonly method: string;
  readonly path: string;
  readonly status: number;
  readonly correlationId: string;
}

export type Signal = "repeated_click" | "repeated_failure";

const MAX_TRAIL = 30;
const trail: TrailEntry[] = [];
const listeners = new Set<(signal: Signal, detail: string) => void>();

/** The paths that never raise a signal or enter a report. */
const QUIET = new Set(["/api/problem-reports"]);

/** Record one finished request. */
export function recordRequest(entry: TrailEntry): void {
  if (QUIET.has(entry.path.split("?")[0] ?? "")) return;
  trail.push({ ...entry, path: entry.path.split("?")[0] ?? "" });
  if (trail.length > MAX_TRAIL) trail.shift();
  if (entry.method !== "GET" && entry.status >= 400) {
    noteFailure(entry);
  } else if (entry.method !== "GET") {
    failures.clear();
  }
}

/** The recent requests, oldest first. */
export function recentTrail(): readonly TrailEntry[] {
  return [...trail];
}

/** Listen for a signal. Returns the function that stops listening. */
export function onSignal(listener: (signal: Signal, detail: string) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// One signal per trigger per minute, so a stuck page writes one record, not many.
const raised = new Map<string, number>();

function raise(signal: Signal, detail: string): void {
  const key = `${signal}:${detail}`;
  const now = Date.now();
  const last = raised.get(key) ?? 0;
  if (now - last < 60_000) return;
  raised.set(key, now);
  for (const listener of listeners) listener(signal, detail);
}

// --- Repeated failure ------------------------------------------------------

const failures = new Map<string, number>();

function noteFailure(entry: TrailEntry): void {
  const key = `${entry.method} ${entry.path}`;
  const count = (failures.get(key) ?? 0) + 1;
  failures.set(key, count);
  if (count >= 3) {
    failures.delete(key);
    raise("repeated_failure", key);
  }
}

// --- Repeated click --------------------------------------------------------

let lastTarget: Element | null = null;
let clicks: number[] = [];

/** A short name for the control, for the record. Never its content. */
function controlName(target: Element): string {
  const control = target.closest("button, a, [role=button], input[type=submit]");
  if (control === null) return "";
  const label = control.getAttribute("aria-label") ?? control.textContent ?? "";
  return `${control.tagName.toLowerCase()} ${label.trim().slice(0, 40)}`.trim();
}

/** Install the click watcher once. Returns the function that removes it. */
export function watchClicks(): () => void {
  const onClick = (event: MouseEvent): void => {
    const target = event.target as Element | null;
    if (target === null) return;
    const control = target.closest("button, a, [role=button], input[type=submit]");
    if (control === null) return;
    // A control that opens or closes something is pressed often by design.
    if (control.getAttribute("aria-expanded") !== null) return;
    // So is one that says so. An optional illustration button invites a
    // second and a third press, and pressing it is not a report that
    // something failed to respond.
    if (control.hasAttribute("data-repeat-expected")) return;
    const now = Date.now();
    if (control !== lastTarget) {
      lastTarget = control;
      clicks = [];
    }
    clicks = clicks.filter((at) => now - at < 2000);
    clicks.push(now);
    if (clicks.length >= 3) {
      clicks = [];
      raise("repeated_click", controlName(control));
    }
  };
  document.addEventListener("click", onClick, true);
  return () => document.removeEventListener("click", onClick, true);
}
